/**
 * 69.17 step 1: a ship as it is at a tick — the one accessor every reader of a flying ship asks (the hit shapes, the crash's
 * start, point defence, the anti-dropship missile's lead, the bots' ship marks, the landing intel; the host's drawing, the
 * ship's voice, the marks). Today it answers from the ship's path (core/dropship.ts dropshipPose and its kinematics,
 * core/lighter.ts lighterPose), the same numbers they gave each reader before, bit for bit. 69.17 step 3 adds a piloted
 * craft, answered from its own stepped state through the same fields (its velocity stored, so nothing reads tick + 1; a
 * tick ahead of the state, as the bots' lead asks, extrapolated: core/dropship.ts pilotedPose).
 *
 * Not memoised: a pose reads the planets, which a crater can change within a tick, so each call is the computation its
 * caller made before.
 */
import type { DropshipFlight, GameState, LighterRun, Vec3 } from '../types';
import type { ShipFrame } from './shipBody';
import { DROPSHIP } from '../constants/action';
import { dropshipPose, dropshipKinematics, type DropshipPose } from './dropship';
import { lighterById, lighterPose, type LighterPose } from './lighter';
import { lighterFrame, LIGHTER_ID_BASE, isLighterId } from './lighterBody';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { rightOf } from './surfaceWalk';
import { vecAdd, vecScale, vecNormalize, vecSub, vecDot } from '../math';

/** A flight's speed, acceleration (along its heading) and climb, people a second (a second²): dropshipKinematics'. */
export type ShipKinematics = { speed: number; accel: number; climb: number };

/** A ship at a tick: whose, where, its frame and velocity, and what moves it (a dropship's flight or a lighter's run, on its path). */
export interface ShipNow {
  id: number;
  team: number;
  planetIndex: number;
  /** the pose's point (the ground under it lifted by its height; world) */
  pos: Vec3;
  /** its frame: the hull's origin, up, front, left and the nacelles' tilt */
  frame: ShipFrame;
  /** world units a second (zero for a flight with no kinematics: not in the air) */
  vel: Vec3;
  flight?: { record: DropshipFlight; pose: DropshipPose; kin: ShipKinematics | null };
  lighter?: { record: LighterRun; pose: LighterPose };
}

const ZERO: Vec3 = { x: 0, y: 0, z: 0 };

/** The nacelles' tilt share from a flight's kinematics (0 aft at cruise … 1 upright): the look's rule on the speed; 1 standing. */
export function tiltOf(k: ShipKinematics | null, person: number): number {
  if (!k) return 1;
  const cruise = DROPSHIP.speedPeople * person;
  const c = (x: number) => Math.min(1, Math.max(0, x));
  // (rendering/dropshipLooks.ts nacelleTiltFor: upright at a standstill, aft at cruise, by the speed; the brake and the boost are the look's dressing)
  return c(1 - Math.abs(k.speed) / cruise);
}

/** The nacelles' tilt share for a flight at `tick`: tiltOf its kinematics then. */
export function shipTilt(state: Pick<GameState, 'planets' | 'config'>, f: DropshipFlight, tick: number, person: number): number {
  return tiltOf(dropshipKinematics(state, f, tick), person);
}

/** A posed flight's frame. */
export function flightFrame(pose: DropshipPose, tilt: number): ShipFrame {
  const up = vecNormalize(pose.dir), front = vecNormalize(vecSub(pose.heading, vecScale(up, vecDot(pose.heading, up))));
  return { origin: pose.pos, up, front, left: vecScale(rightOf(front, up), -1), tilt };
}

/** Flight `f` at `tick`, or null when it is not there (not yet called, or gone). */
export function flightNow(state: Pick<GameState, 'planets' | 'config'> & Partial<Pick<GameState, 'shipPads'>>, f: DropshipFlight, tick: number): ShipNow | null {
  const pose = dropshipPose(state, f, tick);
  if (!pose) return null;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  if (f.piloted) {
    // 69.17 step 3: a piloted flight — its body's attitude (banked, pitched), its velocity, the nacelles' tilt the allocation's own
    // (each set from up toward the front: the frame's share, 1 upright … 0 aft; the path's rule, mixing a speed in people with one in world units, is not used)
    const b = f.piloted.body, up = vecNormalize(b.up), front = vecNormalize(b.front);
    const tilt = Math.min(1, Math.max(0, 1 - b.tilt.reduce((s, t) => s + t, 0) / (b.tilt.length || 1) / (Math.PI / 2)));
    const frame: ShipFrame = { origin: pose.pos, up, front, left: vecScale(rightOf(front, up), -1), tilt };
    const kin = { speed: vecDot(b.vel, front) / person, accel: 0, climb: vecDot(b.vel, vecNormalize(pose.dir)) / person };
    return { id: f.id, team: f.team, planetIndex: f.planetIndex, pos: pose.pos, frame, vel: b.vel, flight: { record: f, pose, kin } };
  }
  const kin = dropshipKinematics(state, f, tick);
  const frame = flightFrame(pose, tiltOf(kin, person));
  const vel = kin ? vecAdd(vecScale(frame.front, kin.speed * person), vecScale(frame.up, kin.climb * person)) : ZERO;
  return { id: f.id, team: f.team, planetIndex: f.planetIndex, pos: pose.pos, frame, vel, flight: { record: f, pose, kin } };
}

/** Lighter run `r` at `tick` (its id in the ship shapes LIGHTER_ID_BASE + its own), or null when it is not there. */
export function lighterNow(state: Pick<GameState, 'planets' | 'config'>, r: LighterRun, tick: number): ShipNow | null {
  const pose = lighterPose(state, r, tick);
  if (!pose) return null;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k, frame = lighterFrame(pose);
  return { id: LIGHTER_ID_BASE + r.id, team: r.team, planetIndex: r.planetIndex, pos: pose.pos, frame, vel: vecScale(frame.front, pose.speed * person), lighter: { record: r, pose } };
}

/** Ship `id` at `tick` — a flight (its id) or a lighter (LIGHTER_ID_BASE + its run's) — or null (gone, not there, or a ship parked on its pad). */
export function shipNow(state: Pick<GameState, 'planets' | 'config' | 'dropships'> & Partial<Pick<GameState, 'shipPads' | 'lighters'>>, id: number, tick: number): ShipNow | null {
  if (isLighterId(id)) {
    const r = lighterById(state, id - LIGHTER_ID_BASE);
    return r ? lighterNow(state, r, tick) : null;
  }
  const f = id >= 0 ? state.dropships?.flights.find(x => x.id === id) : undefined;
  return f ? flightNow(state, f, tick) : null;
}
